import React, { useContext, useState, useEffect, useRef } from "react";
import Style from "./SVIDashboard.module.css";
import { Icon, Button, Select } from "antd";
import { useHistory, useLocation } from "react-router-dom";
import AppContext from "../../context/app.context";
import { IAppContext } from "../../interfaces/context.interfaces";
import { IFirebaseAuthUser } from "../../interfaces/authentication.interfaces";
import AuthenticationValidatorLogic from "../../logic/authenticationValidatorLogic";
import * as ROUTES from "../../constants/routes";
import { isDreamUser as checkIsDreamUser } from "../../constants/dream.constants";
import withAuthorization from "../HOC/with.authorization";
import { env } from "../../env";
import QuarterDetailModal from "./QuarterDetailModal";
import TimelapseModal from "./TimelapseModal";
import {
  canonicalObjectConfigKey,
  DEFAULT_SVI_DASHBOARD_OBJECT_ID,
  getDashboardQuartersForYear,
  objectConfigs,
  resolveDashboardObjectId,
  toS3ObjectNumber,
} from "../../services/svidashboard.mock";
import {
  buildDashboardImageDisplayUrl,
  isTiffDashboardKey,
} from "../../services/svidashboard-image.util";

const { Option } = Select;

interface IState {
  selectedYear: number;
  selectedQuarter: number;
  refreshCounter: number;
}

function SVIDashboard(Props: IState) {
  const [selectedYear, setSelectedYear] = useState(2025);
  const [selectedQuarter, setSelectedQuarter] = useState<number | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [imageMap, setImageMap] = useState<{
    [key: string]: {
      src: string;
      date: string | null;
      key?: string;
      displaySrc?: string;
    };
  }>({});
  const [imageLoadErrors, setImageLoadErrors] = useState<Record<string, boolean>>({});
  const [showPolygons, setShowPolygons] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailModalYear, setDetailModalYear] = useState<number>(2025);
  const [detailModalQuarter, setDetailModalQuarter] = useState<number>(4);
  const [detailModalEnableExpertEditing, setDetailModalEnableExpertEditing] =
    useState<boolean>(true);
  const [timelapseVisible, setTimelapseVisible] = useState(false);
  /** dream@: progress-button image replaces main tile image; key `${boxNumber}-${year}-${quarter}` */
  const [dreamProgressImageByTile, setDreamProgressImageByTile] = useState<{
    [tileKey: string]: {
      variant: 'lightGreen' | 'darkGreen';
      src: string;
      date: string | null;
    };
  }>({});
  const [expertEstimations, setExpertEstimations] = useState<{
    [key: string]: number;
  }>({});
  const imageCacheRef = useRef<{
    [key: string]: {
      src: string;
      date: string | null;
      key?: string;
      displaySrc?: string;
    };
  }>({});
  const history = useHistory();
  const location = useLocation();

  // State to track individual year and quarter for each box
  // Format: { [quarterBoxNumber]: { year: number, quarter: number } }
  const [boxSelections, setBoxSelections] = useState<{ [key: number]: { year: number; quarter: number } }>({
    1: { year: 2025, quarter: 1 },
    2: { year: 2025, quarter: 2 },
    3: { year: 2025, quarter: 3 },
    4: { year: 2025, quarter: 4 },
  });
  
  const refresh = () => setRefreshCounter(refreshCounter + 1);

  const appContext: IAppContext = useContext(AppContext);

  // Track previous selectedYear to sync boxes when it changes
  const prevSelectedYearRef = useRef<number>(selectedYear);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const yearParam = params.get("year");
    if (yearParam) {
      const parsed = parseInt(yearParam, 10);
      if (!isNaN(parsed)) {
        setSelectedYear(parsed);
      }
    }
  }, [location.search]);
  
  // Sync box selections when selectedYear changes from the table
  useEffect(() => {
    const prevYear = prevSelectedYearRef.current;
    
    // Only update if selectedYear actually changed
    if (prevYear !== selectedYear) {
      setBoxSelections((prev) => {
        const newSelections = { ...prev };
        let hasChanges = false;
        
        Object.keys(newSelections).forEach((boxNum) => {
          const boxNumber = parseInt(boxNum);
          const currentSelection = newSelections[boxNumber];
          // Update boxes that are using the PREVIOUS selectedYear and their default quarter
          // This means they haven't been manually changed, so sync them with the new table selection
          if (currentSelection.year === prevYear && currentSelection.quarter === boxNumber) {
            // Only update if the box is still in its default state (using previous year and quarter matches box number)
            newSelections[boxNumber] = {
              year: selectedYear,
              quarter: boxNumber,
            };
            hasChanges = true;
          }
        });
        
        return hasChanges ? newSelections : prev;
      });
      
      // Update the ref
      prevSelectedYearRef.current = selectedYear;
    }
  }, [selectedYear]);

  useEffect(() => {
    setDreamProgressImageByTile({});
    setImageLoadErrors({});
  }, [location.search]);

  // Load images for all box selections from backend S3-backed API
  useEffect(() => {
    let cancelled = false;

    const loadImages = async () => {
      const services = appContext?.services;
      if (!services?.sviDashboardService) {
        return;
      }

      const searchParams = new URLSearchParams(location.search);
      const dashboardObj = resolveDashboardObjectId(
        searchParams.get("fromObjectStats"),
      );
      const objectNumber = toS3ObjectNumber(dashboardObj);

      const newImageMap: {
        [key: string]: {
          src: string;
          date: string | null;
          key?: string;
          displaySrc?: string;
        };
      } = {};
      const entries: Array<{
        cacheKey: string;
        tileKey: string;
        year: number;
        quarter: number;
        useToggleImage: boolean;
      }> = [];

      Object.keys(boxSelections).forEach((boxNum) => {
        const boxNumber = parseInt(boxNum, 10);
        const { year, quarter } = boxSelections[boxNumber];
        const useToggleImage = showPolygons && year === 2025 && quarter === 1;
        const cacheKey = `${objectNumber}-${boxNumber}-${year}-${quarter}-${
          useToggleImage ? "toggle" : "normal"
        }`;
        const tileKey = `${boxNumber}-${year}-${quarter}`;
        entries.push({
          cacheKey,
          tileKey,
          year,
          quarter,
          useToggleImage,
        });
      });

      // First, hydrate from cache if available
      entries.forEach(({ cacheKey, tileKey }) => {
        const cached = imageCacheRef.current[cacheKey];
        if (cached) {
          newImageMap[tileKey] = cached;
        }
      });

      // Fetch any missing images from backend
      await Promise.all(
        entries.map(async ({ cacheKey, tileKey, year, quarter, useToggleImage }) => {
          if (imageCacheRef.current[cacheKey]) {
            return;
          }
            try {
              const response = await services.sviDashboardService.getDashboardImage(
              objectNumber,
              year,
              quarter,
              useToggleImage,
            );
            const data = response.data;
            if (data && data.success && data.url && !cancelled) {
              const displaySrc = isTiffDashboardKey(data.key)
                ? buildDashboardImageDisplayUrl(
                    objectNumber,
                    year,
                    quarter,
                    useToggleImage,
                  )
                : undefined;
              const imageInfo = {
                src: data.url,
                date: data.date || null,
                key: data.key,
                displaySrc,
              };
              imageCacheRef.current[cacheKey] = imageInfo;
              newImageMap[tileKey] = imageInfo;
            }
          } catch (e) {
            // On error, leave image missing; placeholder will be shown
          }
        }),
      );

      if (!cancelled) {
        setImageMap((prev) => ({ ...prev, ...newImageMap }));
        setImageLoadErrors((prev) => {
          const next = { ...prev };
          entries.forEach(({ tileKey }) => {
            if (newImageMap[tileKey]) {
              delete next[tileKey];
            }
          });
          return next;
        });
      }
    };

    loadImages();

    return () => {
      cancelled = true;
    };
  }, [boxSelections, showPolygons, appContext, location.search]);

  useEffect(() => {
    setDreamProgressImageByTile((prev) => {
      const next: typeof prev = {};
      Object.keys(prev).forEach((key) => {
        const [b, y, q] = key.split("-").map((n) => parseInt(n, 10));
        const sel = boxSelections[b];
        if (sel && sel.year === y && sel.quarter === q) {
          next[key] = prev[key];
        }
      });
      if (Object.keys(next).length === Object.keys(prev).length) {
        return prev;
      }
      return next;
    });
  }, [boxSelections]);

  // Fetch expert estimations for displayed quarters
  useEffect(() => {
    let cancelled = false;
    const services = appContext?.services;
    if (!services?.sviDashboardService) {
      return;
    }
    const fetchEstimations = async () => {
      const newEstimations: { [key: string]: number } = {};
      await Promise.all(
        [1, 2, 3, 4].map(async (boxNum) => {
          const selection = boxSelections[boxNum];
          if (!selection) return;
          const { year, quarter } = selection;
          try {
            const response = await services.sviDashboardService.getQuarterProgress(
              year,
              quarter,
            );
            const payload = response.data;
            if (
              !cancelled &&
              payload?.success &&
              payload?.data?.expertProgressPercent != null
            ) {
              newEstimations[`${year}-${quarter}`] =
                payload.data.expertProgressPercent;
            }
          } catch {
            // Ignore errors; use default button text
          }
        }),
      );
      if (!cancelled) {
        setExpertEstimations((prev) => ({ ...prev, ...newEstimations }));
      }
    };
    fetchEstimations();
    return () => {
      cancelled = true;
    };
  }, [boxSelections, appContext, refreshCounter, location.search]);

  // as long as the user data has not yet been retrieved...
  if ((appContext as any)?.userData == null) {
    // ... refresh this component once a second
    window.setTimeout(refresh, 1000);
    return null;
  }

  // Get user email
  const userEmail = appContext?.services?.firebaseService?.auth?.currentUser?.email || '';
  const isDreamUser = checkIsDreamUser(userEmail);

  const urlParams = new URLSearchParams(window.location.search);
  const eventId = urlParams.get("eventId");
  const fromMap = urlParams.get("fromMap") === "true";
  const fromObjectStats = urlParams.get("fromObjectStats");
  const dashboardObjectId = resolveDashboardObjectId(fromObjectStats);
  const headerTitle =
    objectConfigs[dashboardObjectId]?.headerTitle ??
    objectConfigs[DEFAULT_SVI_DASHBOARD_OBJECT_ID].headerTitle;

  const objectNumber = toS3ObjectNumber(dashboardObjectId);

  const handleDreamProgressClick = async (
    boxNumber: number,
    variant: 'lightGreen' | 'darkGreen',
    boxYear: number,
    boxQuarter: number,
  ) => {
    const tileKey = `${boxNumber}-${boxYear}-${boxQuarter}`;

    // Toggle behavior: clicking the same variant again reverts to normal image.
    const existing = dreamProgressImageByTile[tileKey];
    if (existing?.variant === variant) {
      setDreamProgressImageByTile((prev) => {
        const next = { ...prev };
        delete next[tileKey];
        return next;
      });
      return;
    }

    const services = appContext?.services;
    if (!services?.sviDashboardService) {
      return;
    }
    try {
      const response = await services.sviDashboardService.getProgressVariantImage(
        objectNumber,
        boxYear,
        boxQuarter,
        variant,
      );
      const data = response.data;
      if (data?.success && data.url) {
        setDreamProgressImageByTile((prev) => ({
          ...prev,
          [tileKey]: {
            variant,
            src: data.url,
            date: data.date ?? null,
          },
        }));
      }
    } catch {
      // Missing progress image or request error; keep current tile image
    }
  };

  const years = [2021, 2022, 2023, 2024, 2025, 2026];
  
  // Function to get status for a year based on selected year
  const getYearStatus = (year: number): string => {
    if (year === 2026) {
      return "N/A";
    }
    return selectedYear === year ? "Displayed" : "OPEN";
  };

  const getQuarterTitle = (boxNumber: number) => {
    const { year, quarter } = boxSelections[boxNumber] || { year: selectedYear, quarter: boxNumber };
    // Special case for 2021: Q2 and Q3 show "N/A"
    if (year === 2021 && (quarter === 2 || quarter === 3)) {
      return "N/A";
    }
    return `Scene Quarter ${quarter}, ${year}`;
  };
  
  const handleBoxYearChange = (boxNumber: number, year: number) => {
    setBoxSelections((prev) => ({
      ...prev,
      [boxNumber]: {
        ...prev[boxNumber],
        year,
      },
    }));
  };
  
  const handleBoxQuarterChange = (boxNumber: number, quarter: number) => {
    setBoxSelections((prev) => ({
      ...prev,
      [boxNumber]: {
        ...prev[boxNumber],
        quarter,
      },
    }));
  };
  
  const handleBoxReset = (boxNumber: number) => {
    setBoxSelections((prev) => ({
      ...prev,
      [boxNumber]: {
        year: selectedYear,
        quarter: boxNumber,
      },
    }));
  };

  const getQuartersForYear = (year: number) =>
    getDashboardQuartersForYear(dashboardObjectId, year);

  const quarters = getQuartersForYear(selectedYear);

  const handleYearClick = (year: number) => {
    if (year !== 2026) {
      const previousYear = selectedYear;
      setSelectedYear(year);
      // Update all boxes that are using the previous selectedYear and are in default state
      setBoxSelections((prev) => {
        const newSelections = { ...prev };
        Object.keys(newSelections).forEach((boxNum) => {
          const boxNumber = parseInt(boxNum);
          const currentSelection = newSelections[boxNumber];
          // If box is using the previous selectedYear and its quarter matches the box number (default state), update it
          if (currentSelection.year === previousYear && currentSelection.quarter === boxNumber) {
            newSelections[boxNumber] = {
              year: year,
              quarter: boxNumber,
            };
          }
        });
        return newSelections;
      });
      // Update the ref immediately
      prevSelectedYearRef.current = year;
    }
  };

  const handleBackClick = () => {
    if (fromObjectStats) {
      const backObjectId =
        canonicalObjectConfigKey(fromObjectStats) ?? fromObjectStats;
      history.push(
        ROUTES.OBJECT_STATS.route.replace(":objectId", backObjectId),
      );
    } else if (fromMap && eventId) {
      // Navigate back to map with event ID to restore modal
      window.location.href = env.REACT_APP_GIS_PREFIX + ROUTES.MAP.route + `?eventId=${eventId}`;
    } else {
      // Default back to map
      window.location.href = env.REACT_APP_GIS_PREFIX + ROUTES.MAP.route;
    }
  };

  const handleOpenDetailModal = (
    year: number,
    quarter: number,
    enableExpertEditing: boolean,
  ) => {
    setDetailModalYear(year);
    setDetailModalQuarter(quarter);
    setDetailModalEnableExpertEditing(enableExpertEditing);
    setDetailModalVisible(true);
  };

  const handleCloseDetailModal = () => {
    setDetailModalVisible(false);
  };

  const handleOpenStatistics = () => {
    setDetailModalVisible(false);
    history.push(ROUTES.SVI_DASHBOARD_STATS.route);
  };

  return (
    <div className={Style.dashboardContainer}>
      {(fromMap || fromObjectStats) && (
        <div style={{ position: 'absolute', top: '10px', left: '10px', zIndex: 1000 }}>
          <Button 
            type="default" 
            onClick={handleBackClick}
            style={{ marginBottom: '10px' }}
          >
            <Icon type="arrow-left" /> Back
          </Button>
        </div>
      )}
      <div className={Style.header} style={(fromMap || fromObjectStats) ? { marginTop: '50px' } : {}}>
        <div className={Style.headerLeft}>
          <h2 className={Style.yearTitle}>{headerTitle}</h2>
          <table className={Style.yearTable}>
            <tbody>
              <tr>
                {years.map((year) => (
                  <td
                    key={year}
                    className={`${Style.yearHeader} ${
                      selectedYear === year ? Style.selectedYear : ""
                    }`}
                    onClick={() => handleYearClick(year)}
                  >
                    {year}
                  </td>
                ))}
              </tr>
              <tr>
                {years.map((year) => (
                  <td
                    key={`status-${year}`}
                    className={`${Style.yearStatus} ${
                      selectedYear === year ? Style.selectedYearStatus : ""
                    }`}
                  >
                    {getYearStatus(year)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <div
          className={Style.videoIcon}
          onClick={() => setTimelapseVisible(true)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              setTimelapseVisible(true);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Open timelapse"
        >
          <Icon type="video-camera" style={{ fontSize: "24px" }} />
        </div>
      </div>

      <div className={Style.quartersGrid}>
        {quarters.map((quarter) => {
          const boxNumber = quarter.number;
          const boxSelection = boxSelections[boxNumber] || { year: selectedYear, quarter: boxNumber };
          const boxYear = boxSelection.year;
          const boxQuarter = boxSelection.quarter;
          
          // Get quarter data for the selected year and quarter
          const yearQuarters = getQuartersForYear(boxYear);
          const boxQuarterData = yearQuarters.find(q => q.number === boxQuarter) || quarter;
          const quarterActionLabel =
            isDreamUser && boxYear === 2025 && boxQuarter === 1
              ? boxQuarterData.buttonText
              : expertEstimations[`${boxYear}-${boxQuarter}`] != null
                ? `E${expertEstimations[`${boxYear}-${boxQuarter}`]}`
                : boxQuarterData.buttonText;

          return (
            <div key={boxNumber} className={Style.quarterBox}>
              <div className={Style.quarterHeader}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <div />
                  {(boxYear !== selectedYear || boxQuarter !== boxNumber) && (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => handleBoxReset(boxNumber)}
                      style={{ padding: '0', height: 'auto', fontSize: '11px', color: '#1890ff' }}
                    >
                      Reset
                    </Button>
                  )}
                </div>
                <Select
                  value={boxYear}
                  onChange={(value) => handleBoxYearChange(boxNumber, value)}
                  style={{ width: '100%', marginBottom: '8px' }}
                  size="small"
                >
                  {years.filter(y => y !== 2026).map((year) => (
                    <Option key={year} value={year}>
                      {year}
                    </Option>
                  ))}
                </Select>
                <Select
                  value={boxQuarter}
                  onChange={(value) => handleBoxQuarterChange(boxNumber, value)}
                  style={{ width: '100%' }}
                  size="small"
                >
                  {[1, 2, 3, 4].map((q) => (
                    <Option key={q} value={q}>
                      Quarter {q}
                    </Option>
                  ))}
                </Select>
              </div>
              <div className={Style.quarterContent}>
                <h3 className={Style.quarterTitle}>
                  {getQuarterTitle(boxNumber)}
                </h3>
                <div className={Style.imageContainer}>
                  {(() => {
                    const tileKey = `${boxNumber}-${boxYear}-${boxQuarter}`;
                    const base = imageMap[tileKey];
                    const progress =
                      isDreamUser && dreamProgressImageByTile[tileKey]
                        ? dreamProgressImageByTile[tileKey]
                        : undefined;
                    const shown = progress || base;
                    const showMissingImage = !shown || imageLoadErrors[tileKey];

                    if (showMissingImage) {
                      return (
                        <div className={Style.imagePlaceholder}>N/A</div>
                      );
                    }

                    return (
                      <>
                        <img
                          src={shown.src}
                          alt={getQuarterTitle(boxNumber)}
                          className={Style.quarterImage}
                          onError={() => {
                              const currentImage = base;
                              if (
                                currentImage?.displaySrc &&
                                shown.src !== currentImage.displaySrc
                              ) {
                                setImageMap((prev) => ({
                                  ...prev,
                                  [tileKey]: {
                                    ...prev[tileKey],
                                    src: currentImage.displaySrc!,
                                  },
                                }));
                                return;
                              }
                              setImageLoadErrors((prev) => ({
                                ...prev,
                                [tileKey]: true,
                              }));
                            }}
                          />
                        {shown.date ? (
                          <span className={Style.imageDate}>{shown.date}</span>
                        ) : null}
                      </>
                    );
                  })()}
                </div>
              </div>
              <div className={Style.quarterFooter}>
                <div className={Style.footerLeft}>
                  {boxQuarter === 1 && (
                    <div className={Style.buttonInfo}>
                      <button
                        className={Style.progressButtonLight}
                        onClick={() => {
                          if (isDreamUser) {
                            void handleDreamProgressClick(
                              boxNumber,
                              'lightGreen',
                              boxYear,
                              boxQuarter,
                            );
                          }
                        }}
                      >
                        Progress
                      </button>
                      <span>changes to previous Quarter: {boxQuarterData.prevQuarterPercent}</span>
                    </div>
                  )}
                  {boxQuarter === 2 && (
                    <>
                      {boxQuarterData.hasAlert ? (
                        <div className={`${Style.buttonInfo} ${Style.alert}`}>
                          <button
                            className={Style.progressButtonLight}
                            onClick={() => {
                              if (isDreamUser) {
                                void handleDreamProgressClick(
                                  boxNumber,
                                  'lightGreen',
                                  boxYear,
                                  boxQuarter,
                                );
                              } else {
                                handleOpenDetailModal(
                                  boxYear,
                                  boxQuarter,
                                  false,
                                );
                              }
                            }}
                          >
                            Progress
                          </button>
                          <span>changes to prev. Q: {boxQuarterData.prevQuarterPercent} / ALERT</span>
                        </div>
                      ) : (
                        <div className={Style.buttonInfo}>
                          <button
                            className={Style.progressButtonLight}
                            onClick={() => {
                              if (isDreamUser) {
                                void handleDreamProgressClick(
                                  boxNumber,
                                  'lightGreen',
                                  boxYear,
                                  boxQuarter,
                                );
                              } else {
                                handleOpenDetailModal(
                                  boxYear,
                                  boxQuarter,
                                  false,
                                );
                              }
                            }}
                          >
                            Progress
                          </button>
                          <span>changes to previous Quarter: {boxQuarterData.prevQuarterPercent}</span>
                        </div>
                      )}
                      {boxQuarterData.startOfYearPercent !== null && (
                        <div className={Style.buttonInfo}>
                          <button
                            className={Style.progressButtonDark}
                            onClick={() => {
                              if (isDreamUser) {
                                void handleDreamProgressClick(
                                  boxNumber,
                                  'darkGreen',
                                  boxYear,
                                  boxQuarter,
                                );
                              } else {
                                handleOpenDetailModal(
                                  boxYear,
                                  boxQuarter,
                                  false,
                                );
                              }
                            }}
                          >
                            Progress
                          </button>
                          <span>changes to start of the Year: {boxQuarterData.startOfYearPercent}</span>
                        </div>
                      )}
                    </>
                  )}
                  {boxQuarter === 3 && (
                    <>
                      {boxQuarterData.hasAlert ? (
                        <div className={`${Style.buttonInfo} ${Style.alert}`}>
                          <button
                            className={Style.progressButtonLight}
                            onClick={() => {
                              if (isDreamUser) {
                                void handleDreamProgressClick(
                                  boxNumber,
                                  'lightGreen',
                                  boxYear,
                                  boxQuarter,
                                );
                              } else {
                                handleOpenDetailModal(
                                  boxYear,
                                  boxQuarter,
                                  false,
                                );
                              }
                            }}
                          >
                            Progress
                          </button>
                          <span>changes to prev. Q: {boxQuarterData.prevQuarterPercent} / ALERT</span>
                        </div>
                      ) : (
                        <div className={Style.buttonInfo}>
                          <button
                            className={Style.progressButtonLight}
                            onClick={() => {
                              if (isDreamUser) {
                                void handleDreamProgressClick(
                                  boxNumber,
                                  'lightGreen',
                                  boxYear,
                                  boxQuarter,
                                );
                              } else {
                                handleOpenDetailModal(
                                  boxYear,
                                  boxQuarter,
                                  false,
                                );
                              }
                            }}
                          >
                            Progress
                          </button>
                          <span>changes to previous Quarter: {boxQuarterData.prevQuarterPercent}</span>
                        </div>
                      )}
                      {boxQuarterData.startOfYearPercent !== null && (
                        <div className={Style.buttonInfo}>
                          <button
                            className={Style.progressButtonDark}
                            onClick={() => {
                              if (isDreamUser) {
                                void handleDreamProgressClick(
                                  boxNumber,
                                  'darkGreen',
                                  boxYear,
                                  boxQuarter,
                                );
                              } else {
                                handleOpenDetailModal(
                                  boxYear,
                                  boxQuarter,
                                  false,
                                );
                              }
                            }}
                          >
                            Progress
                          </button>
                          <span>changes to start of the Year: {boxQuarterData.startOfYearPercent}</span>
                        </div>
                      )}
                    </>
                  )}
                  {boxQuarter === 4 && (
                    <>
                      {boxQuarterData.hasAlert ? (
                        <div className={`${Style.buttonInfo} ${Style.alert}`}>
                          <button
                            className={Style.progressButtonLight}
                            onClick={() => {
                              if (isDreamUser) {
                                void handleDreamProgressClick(
                                  boxNumber,
                                  'lightGreen',
                                  boxYear,
                                  boxQuarter,
                                );
                              } else {
                                handleOpenDetailModal(
                                  boxYear,
                                  boxQuarter,
                                  false,
                                );
                              }
                            }}
                          >
                            Progress
                          </button>
                          <span>changes to prev. Q: {boxQuarterData.prevQuarterPercent} / ALERT</span>
                        </div>
                      ) : (
                        <div className={Style.buttonInfo}>
                          <button
                            className={Style.progressButtonLight}
                            onClick={() => {
                              if (isDreamUser) {
                                void handleDreamProgressClick(
                                  boxNumber,
                                  'lightGreen',
                                  boxYear,
                                  boxQuarter,
                                );
                              } else {
                                handleOpenDetailModal(
                                  boxYear,
                                  boxQuarter,
                                  false,
                                );
                              }
                            }}
                          >
                            Progress
                          </button>
                          <span>changes to previous Quarter: {boxQuarterData.prevQuarterPercent}</span>
                        </div>
                      )}
                      {boxQuarterData.startOfYearPercent !== null && (
                        <div className={Style.buttonInfo}>
                          <button
                            className={Style.progressButtonDark}
                            onClick={() => {
                              if (isDreamUser) {
                                void handleDreamProgressClick(
                                  boxNumber,
                                  'darkGreen',
                                  boxYear,
                                  boxQuarter,
                                );
                              } else {
                                handleOpenDetailModal(
                                  boxYear,
                                  boxQuarter,
                                  false,
                                );
                              }
                            }}
                          >
                            Progress
                          </button>
                          <span>changes to start of the Year: {boxQuarterData.startOfYearPercent}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className={Style.footerRight}>
                  <button
                    className={`${Style.actionButton}${
                      quarterActionLabel === "E0"
                        ? ` ${Style.actionButtonAlert}`
                        : ""
                    }`}
                    onClick={() => {
                      const enableExpertEditing =
                        boxYear === 2025 &&
                        boxQuarter === 4 &&
                        boxQuarterData.buttonText === "E85";
                      handleOpenDetailModal(
                        boxYear,
                        boxQuarter,
                        enableExpertEditing,
                      );
                    }}
                  >
                    {quarterActionLabel}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <QuarterDetailModal
        visible={detailModalVisible}
        year={detailModalYear}
        quarter={detailModalQuarter}
        onClose={handleCloseDetailModal}
        onOpenStatistics={handleOpenStatistics}
        onExpertEstimationSaved={(y, q, v) =>
          setExpertEstimations((prev) => ({ ...prev, [`${y}-${q}`]: v }))
        }
        enableExpertEditing={detailModalEnableExpertEditing}
      />
      <TimelapseModal
        visible={timelapseVisible}
        objectId={objectNumber}
        onClose={() => setTimelapseVisible(false)}
      />
    </div>
  );
}

const condition = (authUser: IFirebaseAuthUser) =>
  AuthenticationValidatorLogic.isDreamUser(authUser);

export default withAuthorization(condition)(ROUTES.MAP.route)(SVIDashboard);
